// ============================================================================
//  SUPPORT SERVICE

// ============================================================================
// Handles all support ticket operations - create, read, update, messages
//
// Firestore Structure:
// supportTickets/{ticketId}
//   - odid, username, email, category, subject, status, priority
//   - emailUpdates: boolean (user preference)
//   - movedToEmail: boolean
//   - createdAt, updatedAt, resolvedAt
//
// supportTickets/{ticketId}/messages/{messageId}
//   - senderId, senderName, isAdmin, message, createdAt, read
// ============================================================================

import { db } from './firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';

// ============================================================================
// TYPES
// ============================================================================

export type TicketCategory = 'account' | 'payment' | 'gameplay' | 'technical' | 'feedback' | 'other';
export type TicketStatus = 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface SupportTicket {
  id: string;
  odid: string;              // User's Firebase UID
  username: string;
  email: string;
  category: TicketCategory;
  subject: string;           // First message or auto-generated
  status: TicketStatus;
  priority: TicketPriority;
  emailUpdates: boolean;     // User wants email notifications
  movedToEmail: boolean;     // Conversation moved to email
  unreadByUser: number;      // Count of unread admin messages
  unreadByAdmin: number;     // Count of unread user messages
  assignedTo?: string;       // Admin ID if assigned
  assignedName?: string;     // Admin name
  createdAt: Date;
  updatedAt: Date;
  resolvedAt?: Date;
  lastMessageAt?: Date;
  lastMessagePreview?: string;
}

export interface TicketMessage {
  id: string;
  ticketId: string;
  senderId: string;
  senderName: string;
  isAdmin: boolean;
  message: string;
  createdAt: Date;
  read: boolean;
  // For system messages (status changes, etc.)
  isSystem?: boolean;
}

export interface CreateTicketData {
  odid: string;
  username: string;
  email: string;
  category: TicketCategory;
  message: string;
  emailUpdates?: boolean;
}

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  waitingUser: number;
  resolved: number;
  urgent: number;
  unreadTotal: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const TICKET_CATEGORIES: { id: TicketCategory; label: string; icon: string }[] = [
  { id: 'account', label: 'Account Issues', icon: '' },

  { id: 'payment', label: 'Payment & Withdrawals', icon: '' },

  { id: 'gameplay', label: 'Gameplay & Rules', icon: '🃏' },
  { id: 'technical', label: 'Technical Problems', icon: '' },

  { id: 'feedback', label: 'Feedback & Suggestions', icon: '' },

  { id: 'other', label: 'Other', icon: '' },

];

export const TICKET_STATUSES: { id: TicketStatus; label: string; color: string }[] = [
  { id: 'open', label: 'Open', color: 'blue' },
  { id: 'in_progress', label: 'In Progress', color: 'yellow' },
  { id: 'waiting_user', label: 'Waiting on User', color: 'purple' },
  { id: 'resolved', label: 'Resolved', color: 'green' },
  { id: 'closed', label: 'Closed', color: 'gray' },
];

export const TICKET_PRIORITIES: { id: TicketPriority; label: string; color: string }[] = [
  { id: 'low', label: 'Low', color: 'gray' },
  { id: 'medium', label: 'Medium', color: 'blue' },
  { id: 'high', label: 'High', color: 'orange' },
  { id: 'urgent', label: 'Urgent', color: 'red' },
];

// Auto-assign priority based on category
const CATEGORY_PRIORITY: Record<TicketCategory, TicketPriority> = {
  payment: 'high',
  account: 'medium',
  technical: 'medium',
  gameplay: 'low',
  feedback: 'low',
  other: 'low',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const convertTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  return new Date(timestamp);
};

const generateSubject = (category: TicketCategory, message: string): string => {
  const prefix = TICKET_CATEGORIES.find(c => c.id === category)?.label || 'Support';
  const preview = message.substring(0, 50).trim();
  return `${prefix}: ${preview}${message.length > 50 ? '...' : ''}`;
};

// ============================================================================
// TICKET OPERATIONS
// ============================================================================

/**
 * Create a new support ticket with initial message
 */
export const createTicket = async (data: CreateTicketData): Promise<string> => {
  const ticketRef = collection(db, 'supportTickets');
  
  const ticketData = {
    odid: data.odid,
    username: data.username,
    email: data.email,
    category: data.category,
    subject: generateSubject(data.category, data.message),
    status: 'open' as TicketStatus,
    priority: CATEGORY_PRIORITY[data.category],
    emailUpdates: data.emailUpdates ?? true,
    movedToEmail: false,
    unreadByUser: 0,
    unreadByAdmin: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessagePreview: data.message.substring(0, 100),
  };

  const ticketDoc = await addDoc(ticketRef, ticketData);

  // Add initial message
  const messagesRef = collection(db, 'supportTickets', ticketDoc.id, 'messages');
  await addDoc(messagesRef, {
    ticketId: ticketDoc.id,
    senderId: data.odid,
    senderName: data.username,
    isAdmin: false,
    message: data.message,
    createdAt: serverTimestamp(),
    read: false,
  });

  console.log('🎫 Support ticket created:', ticketDoc.id);
  return ticketDoc.id;
};

/**
 * Get a single ticket by ID
 */
export const getTicket = async (ticketId: string): Promise<SupportTicket | null> => {
  const ticketRef = doc(db, 'supportTickets', ticketId);
  const ticketDoc = await getDoc(ticketRef);

  if (!ticketDoc.exists()) return null;

  const data = ticketDoc.data();
  return {
    id: ticketDoc.id,
    ...data,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    resolvedAt: data.resolvedAt ? convertTimestamp(data.resolvedAt) : undefined,
    lastMessageAt: data.lastMessageAt ? convertTimestamp(data.lastMessageAt) : undefined,
  } as SupportTicket;
};

/**
 * Get all tickets for a user
 */
export const getUserTickets = async (odid: string): Promise<SupportTicket[]> => {
  const ticketsRef = collection(db, 'supportTickets');
  const q = query(
    ticketsRef,
    where('odid', '==', odid),
    orderBy('updatedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      resolvedAt: data.resolvedAt ? convertTimestamp(data.resolvedAt) : undefined,
      lastMessageAt: data.lastMessageAt ? convertTimestamp(data.lastMessageAt) : undefined,
    } as SupportTicket;
  });
};

/**
 * Get all tickets (admin)
 */
export const getAllTickets = async (
  statusFilter?: TicketStatus,
  priorityFilter?: TicketPriority,
  limitCount: number = 100
): Promise<SupportTicket[]> => {
  const ticketsRef = collection(db, 'supportTickets');
  let q = query(ticketsRef, orderBy('updatedAt', 'desc'), limit(limitCount));

  if (statusFilter) {
    q = query(ticketsRef, where('status', '==', statusFilter), orderBy('updatedAt', 'desc'), limit(limitCount));
  }

  const snapshot = await getDocs(q);
  
  let tickets = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      resolvedAt: data.resolvedAt ? convertTimestamp(data.resolvedAt) : undefined,
      lastMessageAt: data.lastMessageAt ? convertTimestamp(data.lastMessageAt) : undefined,
    } as SupportTicket;
  });

  // Filter by priority in memory (Firestore doesn't allow multiple inequality filters)
  if (priorityFilter) {
    tickets = tickets.filter(t => t.priority === priorityFilter);
  }

  return tickets;
};

/**
 * Subscribe to tickets (real-time)
 */
export const subscribeToUserTickets = (
  odid: string,
  callback: (tickets: SupportTicket[]) => void
): (() => void) => {
  const ticketsRef = collection(db, 'supportTickets');
  const q = query(
    ticketsRef,
    where('odid', '==', odid),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const tickets = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        resolvedAt: data.resolvedAt ? convertTimestamp(data.resolvedAt) : undefined,
        lastMessageAt: data.lastMessageAt ? convertTimestamp(data.lastMessageAt) : undefined,
      } as SupportTicket;
    });
    callback(tickets);
  });
};

/**
 * Subscribe to all tickets (admin real-time)
 */
export const subscribeToAllTickets = (
  callback: (tickets: SupportTicket[]) => void
): (() => void) => {
  const ticketsRef = collection(db, 'supportTickets');
  const q = query(ticketsRef, orderBy('updatedAt', 'desc'), limit(200));

  return onSnapshot(q, (snapshot) => {
    const tickets = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        resolvedAt: data.resolvedAt ? convertTimestamp(data.resolvedAt) : undefined,
        lastMessageAt: data.lastMessageAt ? convertTimestamp(data.lastMessageAt) : undefined,
      } as SupportTicket;
    });
    callback(tickets);
  });
};

/**
 * Update ticket status
 */
export const updateTicketStatus = async (
  ticketId: string,
  status: TicketStatus,
  adminName?: string
): Promise<void> => {
  const ticketRef = doc(db, 'supportTickets', ticketId);
  
  const updateData: any = {
    status,
    updatedAt: serverTimestamp(),
  };

  if (status === 'resolved' || status === 'closed') {
    updateData.resolvedAt = serverTimestamp();
  }

  await updateDoc(ticketRef, updateData);

  // Add system message
  const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
  const statusLabel = TICKET_STATUSES.find(s => s.id === status)?.label || status;
  
  await addDoc(messagesRef, {
    ticketId,
    senderId: 'system',
    senderName: adminName || 'System',
    isAdmin: true,
    isSystem: true,
    message: `Ticket status changed to: ${statusLabel}`,
    createdAt: serverTimestamp(),
    read: false,
  });

  console.log(`🎫 Ticket ${ticketId} status updated to ${status}`);
};

/**
 * Update ticket priority
 */
export const updateTicketPriority = async (
  ticketId: string,
  priority: TicketPriority
): Promise<void> => {
  const ticketRef = doc(db, 'supportTickets', ticketId);
  await updateDoc(ticketRef, {
    priority,
    updatedAt: serverTimestamp(),
  });
  console.log(`🎫 Ticket ${ticketId} priority updated to ${priority}`);
};

/**
 * Assign ticket to admin
 */
export const assignTicket = async (
  ticketId: string,
  adminId: string,
  adminName: string
): Promise<void> => {
  const ticketRef = doc(db, 'supportTickets', ticketId);
  await updateDoc(ticketRef, {
    assignedTo: adminId,
    assignedName: adminName,
    status: 'in_progress',
    updatedAt: serverTimestamp(),
  });
  console.log(`🎫 Ticket ${ticketId} assigned to ${adminName}`);
};

/**
 * Move conversation to email
 */
export const moveToEmail = async (ticketId: string): Promise<void> => {
  const ticketRef = doc(db, 'supportTickets', ticketId);
  await updateDoc(ticketRef, {
    movedToEmail: true,
    updatedAt: serverTimestamp(),
  });

  // Add system message
  const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
  await addDoc(messagesRef, {
    ticketId,
    senderId: 'system',
    senderName: 'System',
    isAdmin: true,
    isSystem: true,
    message: 'This conversation has been moved to email. Please check your inbox for further updates.',

    createdAt: serverTimestamp(),
    read: false,
  });

  console.log(`🎫 Ticket ${ticketId} moved to email`);
};

/**
 * Update user email preference
 */
export const updateEmailPreference = async (
  ticketId: string,
  emailUpdates: boolean
): Promise<void> => {
  const ticketRef = doc(db, 'supportTickets', ticketId);
  await updateDoc(ticketRef, {
    emailUpdates,
    updatedAt: serverTimestamp(),
  });
};

// ============================================================================
// MESSAGE OPERATIONS
// ============================================================================

/**
 * Send a message in a ticket
 */
export const sendMessage = async (
  ticketId: string,
  senderId: string,
  senderName: string,
  message: string,
  isAdmin: boolean
): Promise<string> => {
  const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
  
  const messageDoc = await addDoc(messagesRef, {
    ticketId,
    senderId,
    senderName,
    isAdmin,
    message,
    createdAt: serverTimestamp(),
    read: false,
  });

  // Update ticket
  const ticketRef = doc(db, 'supportTickets', ticketId);
  const updateData: any = {
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessagePreview: message.substring(0, 100),
  };

  if (isAdmin) {
    updateData.unreadByUser = (await getDoc(ticketRef)).data()?.unreadByUser + 1 || 1;
    updateData.status = 'waiting_user';
  } else {
    updateData.unreadByAdmin = (await getDoc(ticketRef)).data()?.unreadByAdmin + 1 || 1;
    // If user replies to waiting_user, change back to in_progress
    const currentStatus = (await getDoc(ticketRef)).data()?.status;
    if (currentStatus === 'waiting_user' || currentStatus === 'resolved') {
      updateData.status = 'open';
    }
  }

  await updateDoc(ticketRef, updateData);

  console.log(`💬 Message sent in ticket ${ticketId}`);
  return messageDoc.id;
};

/**
 * Get messages for a ticket
 */
export const getTicketMessages = async (ticketId: string): Promise<TicketMessage[]> => {
  const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
  const q = query(messagesRef, orderBy('createdAt', 'asc'));

  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: convertTimestamp(data.createdAt),
    } as TicketMessage;
  });
};

/**
 * Subscribe to messages (real-time)
 */
export const subscribeToMessages = (
  ticketId: string,
  callback: (messages: TicketMessage[]) => void
): (() => void) => {
  const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
  const q = query(messagesRef, orderBy('createdAt', 'asc'));

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
      } as TicketMessage;
    });
    callback(messages);
  });
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (
  ticketId: string,
  isAdmin: boolean
): Promise<void> => {
  try {
    //  SIMPLIFIED: Just reset the unread counter directly

    // The individual message 'read' field is less important than the counter
    const ticketRef = doc(db, 'supportTickets', ticketId);
    
    if (isAdmin) {
      await updateDoc(ticketRef, { unreadByAdmin: 0 });
    } else {
      await updateDoc(ticketRef, { unreadByUser: 0 });
    }
    
    console.log(`✅ [SUPPORT] Marked ticket ${ticketId} as read (isAdmin=${isAdmin})`);
    
    // Optionally mark individual messages as read (fire and forget, no composite index needed)
    try {
      const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
      const q = query(messagesRef, where('read', '==', false));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const batch = writeBatch(db);
        snapshot.docs.forEach(msgDoc => {
          const msgData = msgDoc.data();
          // Only mark opposite party's messages as read
          if (msgData.isAdmin !== isAdmin) {
            batch.update(msgDoc.ref, { read: true });
          }
        });
        await batch.commit();
      }
    } catch (msgErr) {
      // Non-critical - counter is already reset
      console.warn('Could not mark individual messages:', msgErr);
    }
  } catch (error) {
    console.error('❌ [SUPPORT] Failed to mark as read:', error);
    throw error;
  }
};

// ============================================================================
// STATS
// ============================================================================

/**
 * Get ticket statistics (admin)
 */
export const getTicketStats = async (): Promise<TicketStats> => {
  const ticketsRef = collection(db, 'supportTickets');
  const snapshot = await getDocs(ticketsRef);

  const stats: TicketStats = {
    total: 0,
    open: 0,
    inProgress: 0,
    waitingUser: 0,
    resolved: 0,
    urgent: 0,
    unreadTotal: 0,
  };

  snapshot.docs.forEach(doc => {
    const data = doc.data();
    stats.total++;
    
    switch (data.status) {
      case 'open': stats.open++; break;
      case 'in_progress': stats.inProgress++; break;
      case 'waiting_user': stats.waitingUser++; break;
      case 'resolved': 
      case 'closed': stats.resolved++; break;
    }

    if (data.priority === 'urgent') stats.urgent++;
    stats.unreadTotal += data.unreadByAdmin || 0;
  });

  return stats;
};

/**
 * Get unread count for user
 */
export const getUserUnreadCount = async (odid: string): Promise<number> => {
  const ticketsRef = collection(db, 'supportTickets');
  const q = query(ticketsRef, where('odid', '==', odid));
  const snapshot = await getDocs(q);

  let unread = 0;
  snapshot.docs.forEach(doc => {
    unread += doc.data().unreadByUser || 0;
  });

  return unread;
};

/**
 * Subscribe to unread count (user)
 */
export const subscribeToUnreadCount = (
  odid: string,
  callback: (count: number) => void
): (() => void) => {
  const ticketsRef = collection(db, 'supportTickets');
  const q = query(ticketsRef, where('odid', '==', odid));

  return onSnapshot(q, (snapshot) => {
    let unread = 0;
    snapshot.docs.forEach(doc => {
      unread += doc.data().unreadByUser || 0;
    });
    callback(unread);
  });
};

// ============================================================================
// EXPORT
// ============================================================================

const supportService = {
  // Tickets
  createTicket,
  getTicket,
  getUserTickets,
  getAllTickets,
  subscribeToUserTickets,
  subscribeToAllTickets,
  updateTicketStatus,
  updateTicketPriority,
  assignTicket,
  moveToEmail,
  updateEmailPreference,
  
  // Messages
  sendMessage,
  getTicketMessages,
  subscribeToMessages,
  markMessagesAsRead,
  
  // Stats
  getTicketStats,
  getUserUnreadCount,
  subscribeToUnreadCount,
  
  // Constants
  TICKET_CATEGORIES,
  TICKET_STATUSES,
  TICKET_PRIORITIES,
};

export default supportService;